import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { configureAtomDevtools } from '../utils/tracking';
import { createPersistedStorage } from '../utils/persistence';

const LAUNCHED_WORKSPACES_STORAGE_KEY = 'localagent-launched-workspaces';
const LAUNCHED_WORKSPACES_STORAGE_VERSION = 1;

const LEGACY_ACTIVE_WORKSPACE_KEY = 'localagent-active-workspace';

function migrateLaunchedWorkspaces(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string');
  }
  return [];
}

function migrateFromLegacyActiveWorkspace(initial: string[]): string[] {
  if (initial.length > 0) return initial;

  try {
    const raw = localStorage.getItem(LEGACY_ACTIVE_WORKSPACE_KEY);
    if (!raw) return initial;

    const parsed: unknown = JSON.parse(raw);

    let workspaceId: string | null = null;
    if (typeof parsed === 'string') {
      workspaceId = parsed;
    } else if (parsed && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>;
      if (typeof obj.value === 'string') {
        workspaceId = obj.value;
      } else if (typeof obj.activeWorkspaceId === 'string') {
        workspaceId = obj.activeWorkspaceId;
      }
    }

    if (workspaceId) {
      localStorage.removeItem(LEGACY_ACTIVE_WORKSPACE_KEY);
      return [workspaceId];
    }
  } catch {
    // Ignore parse errors
  }

  return initial;
}

const launchedWorkspaceIdsBaseAtom = atomWithStorage<string[]>(
  LAUNCHED_WORKSPACES_STORAGE_KEY,
  [],
  createPersistedStorage<string[]>({
    version: LAUNCHED_WORKSPACES_STORAGE_VERSION,
    migrate: migrateLaunchedWorkspaces,
  })
);

export const launchedWorkspaceIdsAtom = configureAtomDevtools(
  atom<string[], [string[]], void>(
    (get) => {
      const stored = get(launchedWorkspaceIdsBaseAtom);
      return migrateFromLegacyActiveWorkspace(stored);
    },
    (_get, set, next: string[]) => set(launchedWorkspaceIdsBaseAtom, next)
  ),
  'workspace/launchedWorkspaceIds'
);

export const launchWorkspaceAction = configureAtomDevtools(
  atom<null, [string], void>(null, (get, set, workspaceId) => {
    const current = get(launchedWorkspaceIdsAtom);
    if (!current.includes(workspaceId)) {
      set(launchedWorkspaceIdsAtom, [...current, workspaceId]);
    }
  }),
  'workspace/launchWorkspace'
);

export const dismissWorkspaceAction = configureAtomDevtools(
  atom<null, [string], void>(null, (get, set, workspaceId) => {
    const current = get(launchedWorkspaceIdsAtom);
    set(launchedWorkspaceIdsAtom, current.filter(id => id !== workspaceId));
  }),
  'workspace/dismissWorkspace'
);

